import Modal from "components/EvtKit/modal/Modal"

import { useModal } from "hooks/useModal"

import { memo, useCallback, useEffect, useMemo, useState } from "react"
import styled from "styled-components"
import { useHandleObjList } from "hooks/useHandleObjList"
import { initialBalanceList } from "constants/accounts"
import { AppState, useAppDispatch, useAppSelector } from "reducers/store"
import Input from "components/EvtKit/input/Input"
import Button from "components/EvtKit/button/Button"
import ApiAccounts from "api/ApiAccounts"
import { useMutation, useQueryClient } from "react-query"
import clsx from "clsx"
import Heading from "components/Typography/Heading"
import BodyText from "components/Typography/BodyText"
import IconAddLarge from "components/EvtKit/icons/IconAddLarge"
import IconDeleteSmall from "components/EvtKit/icons/IconDeleteSmall"
import { clearAccount } from "reducers/accounts/accounts-actions"
import { ACCOUNTS_FETCH } from "reducers/accounts/actions-types"
import SelectV2, { Option } from "components/EvtKit/select/SelectV2"
import useHandleApiError from "hooks/useHandleApiErrors"

const BalanceListContainer = styled.ul`
  margin-top: 8px;
  max-height: 400px;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow-y: auto;
  padding: 8px;
`

const BalanceItemContainer = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  height: 100px;
`

const StyledInput = styled(Input)`
  width: 300px;
`

const BalanceSelectContainer = styled.div`
  width: 300px;
`

const BalanceIconDeleteContainer = styled.div`
  margin-left: 8px;
  width: 32px;
  height: 48px;
  display: flex;
  align-items: center;
`

const BalanceFooterContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const BalanceButtonContainer = styled.div`
  width: 160px;
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 12px 28px 0px 24px;
`

const HeaderContainer = styled.div`
  display: grid;
  justify-self: left;
  gap: 16px;
  margin-top: 20px;
`

const AddBalanceContainer = styled.div`
  display: flex;
  justify-content: space-between;
`

const AddBalanceButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background-color: transparent;
  cursor: pointer;

  &.disabled {
    cursor: default;
  }
`

const AddBalanceModal = () => {
  const queryClient = useQueryClient()

  const appDispatch = useAppDispatch()

  const { isOpen, type, closeModal } = useModal()

  const {
    list: balanceList,
    changeElement,
    resetList,
    removeElement,
    addElement
  } = useHandleObjList<App.BalanceList>(initialBalanceList)

  const handleBalanceValueError = useCallback((value) => {
    let error: string | undefined

    if (value !== "" && parseInt(value) <= 0) {
      error = "Valor incorreto"
    }

    return error
  }, [])

  const handleCleanModalState = useCallback(() => {
    resetList()
    appDispatch(clearAccount())
    closeModal()
  }, [resetList])

  const { account } = useAppSelector((x: AppState) => x.accounts)

  const [eventTypes, setEventTypes] = useState<App.SelectOptions[]>([])

  const isBalanceDisabled = useMemo(() => {
    const isBalanceListValid = balanceList.every(
      (item) => item.eventType !== "" && item.count !== "" && !handleBalanceValueError(item.count)
    )

    return !isBalanceListValid
  }, [balanceList])

  const { getErrorMessageAndShowErrorToast } = useHandleApiError()

  const updateBalance = useMutation({
    mutationFn: async (data: App.AccountUpdateBalancePayload) => {
      return new ApiAccounts().updateBalance(data)
    },
    onSuccess: () => {
      handleCleanModalState()
      queryClient.invalidateQueries(ACCOUNTS_FETCH)
    },
    onError: (error) => {
      getErrorMessageAndShowErrorToast(error, "Não foi possível atualizar o balance da conta.")
    }
  })

  const submitBalanceUpdate = useCallback(async () => {
    const balanceUpdatePayload: App.AccountUpdateBalancePayload = {
      accountId: account.id,
      prodPrefixUrl: account.product,
      balance: balanceList.map((item) => ({
        id: item.eventType,
        count: parseInt(item.count)
      }))
    }

    updateBalance.mutate(balanceUpdatePayload)
  }, [account, balanceList])

  const { eventList } = useAppSelector((state) => state.eventTypes)

  useEffect(() => {
    const eventTypesOptions = eventList.map((item) => ({
      value: item.id !== undefined ? item.id : "",
      label: item.name
    }))

    setEventTypes(eventTypesOptions)
  }, [eventList])

  const onSelectChange = useCallback(
    (key: string) => (option?: Option | null) => {
      const optionValue = option?.value

      if (optionValue) {
        changeElement(key, "eventType", optionValue)
      }
    },
    []
  )

  return (
    <Modal openModal={isOpen && type === "addProperty"} closeModal={handleCleanModalState} size="1-lg">
      <Container>
        <Heading variant="h4" color="primary-dark">
          Adicionar crédito
        </Heading>
        <HeaderContainer>
          <Heading variant="h6">
            Esta função permite que você adicione um bônus de uso a esta conta sem custo extra.
          </Heading>
          <AddBalanceContainer>
            <BodyText weight="semi-bold" size="small">
              Definições para o crédito:
            </BodyText>
            <AddBalanceButton type="button" onClick={() => addElement()} className={clsx({ disabled: false })}>
              <IconAddLarge disabled={false} />
            </AddBalanceButton>
          </AddBalanceContainer>
        </HeaderContainer>
        <BalanceListContainer>
          {balanceList.map((item, index) => {
            return (
              <BalanceItemContainer key={index}>
                <BalanceSelectContainer>
                  <SelectV2
                    id="eventType"
                    options={eventTypes}
                    value={item.eventType}
                    onChange={onSelectChange(item.key)}
                    label="Tipo de evento *"
                  />
                </BalanceSelectContainer>

                <StyledInput
                  placeholder="Digite (somente números)"
                  type="number"
                  name="value"
                  label="Limite de uso *"
                  value={item.count}
                  onChange={(e) => changeElement(item.key, "count", e.target.value)}
                  safeBottom
                  error={handleBalanceValueError(item.count)}
                  shrink
                />

                <BalanceIconDeleteContainer onClick={() => removeElement(item.key)}>
                  {index > 0 && (
                    <button>
                      <IconDeleteSmall />
                    </button>
                  )}
                </BalanceIconDeleteContainer>
              </BalanceItemContainer>
            )
          })}
        </BalanceListContainer>
        <BalanceFooterContainer>
          <BalanceButtonContainer>
            <Button
              loading={updateBalance.isLoading}
              type="button"
              label="Adicionar"
              className={clsx("background-gradient fill-width", { disabled: isBalanceDisabled })}
              disabled={isBalanceDisabled || updateBalance.isLoading}
              onClick={submitBalanceUpdate}
            />
          </BalanceButtonContainer>
        </BalanceFooterContainer>
      </Container>
    </Modal>
  )
}

export default memo(AddBalanceModal)
